import { useEffect, useState } from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import { Input, MessageBarBody, MessageBarTitle, useToastController } from '@fluentui/react-components';
import { useTranslation } from 'react-i18next';

import { AppState } from '../../../redux/reducers';
import { Provider } from '../../../models/Entities/Provider/Provider';
import { ErrorType } from '../../../models/HttpError';

// guía de diseño
import { GuideDrawer, GuideDrawerForm, GuideDrawerSection } from '../../../theme/components/GuideDrawer';
import { GuideField } from '../../../theme/components/GuideField';
import { GuideMessageBar } from '../../../theme/components/GuideMessageBar';
import { AppToast, APP_TOASTER_ID, toastTimeout } from '../../../commons/component/AppToast/AppToast.Component';

// lógica
import {
  BUSINESS_NAME_MAX,
  buildInvoiceProviderValues,
  buildNewProvider,
  buildUpdatedProvider,
  isProviderFormDirty,
  normalizeBusinessName,
  validateBusinessName,
  validateTaxId,
} from './ProviderForm.Logic';

// store
import * as ProviderStore from '../../../redux/middleware/ProviderMiddleware';

/** Largo del campo CUIT con separadores ("20-28589697-6"). */
const TAX_ID_INPUT_MAX = 20;

export interface ProviderFormValues {
  businessName: string;
  /** Sólo dígitos; ausente si el CUIT quedó vacío. */
  taxId?: string;
}

export interface IProps {
  isOpen: boolean;
  openPanel: () => void;
  dismissPanel: () => void;
  Provider?: Provider;
  // ---- modo "desde factura" (spec 009, US4; opcionales y retrocompatibles) ----
  /** Valores con que abre el alta (lo detectado en la factura). */
  initialValues?: { businessName?: string | null; taxId?: string | null };
  /** Muestra el CUIT (opcional y editable; verificador sólo si se carga). */
  showTaxId?: boolean;
  /**
   * Guarda con este callback en lugar de despachar `AddProvider` / `UpdateProvider`. Si la promesa se rechaza (ErrorType),
   * el formulario sigue abierto y muestra el error; si se resuelve, se cierra.
   */
  onSubmit?: (values: ProviderFormValues) => Promise<void>;
  /** Línea de contexto del modo factura (p. ej. el nombre del archivo). */
  eyebrow?: string;
  subtitle?: string;
}

/**
 * Alta y edición de un proveedor en un drawer small de la guía: contexto arriba del título
 * ("Nuevo proveedor" o el nombre que se edita), un solo campo obligatorio con ayuda, contador y
 * error debajo, el tipo de persona como campo deshabilitado (lo define el sistema), banner de
 * error del servidor y pie Cancelar / Guardar. Al guardar cierra y avisa con un toast.
 * Modo "desde factura" (spec 009): razón social y CUIT precompletados, CUIT opcional con verificador si se carga y
 * guardado por `onSubmit` (la bandeja crea el proveedor; no se despacha al store).
 */
export const ProviderFormComponent = (props: IProps) => {
  const [t] = useTranslation();
  const dispatch = useDispatch();
  const { dispatchToast } = useToastController(APP_TOASTER_ID);

  const ProviderState = useSelector((appState: AppState) => appState.Provider);

  const [businessName, setBusinessName] = useState<string>('');
  const [taxId, setTaxId] = useState<string>('');
  const [submitted, setSubmitted] = useState<boolean>(false);
  const [externalSaving, setExternalSaving] = useState<boolean>(false);
  const [externalError, setExternalError] = useState<ErrorType | undefined>(undefined);

  const external = !!props.onSubmit;
  const isEdit = !!props.Provider;
  const isSaving = external ? externalSaving : !!(ProviderState?.isAddingNewOne || ProviderState?.isUpdatingOne);
  const saveFailed = !!(ProviderState?.failOnAddingNewOne || ProviderState?.failOnUpdatingOne);
  const saveSucceeded = !!(ProviderState?.successAddingNewOne || ProviderState?.successUpdatingOne);

  const resetForm = (name: string = '', tax: string = '') => {
    setBusinessName(name);
    setTaxId(tax);
    setSubmitted(false);
    setExternalError(undefined);
  };

  // Al abrir (o cambiar de proveedor) se carga el nombre a editar, lo detectado en la factura o se limpia el alta.
  useEffect(() => {
    if (props.isOpen) resetForm(props.Provider?.person?.businessName ?? props.initialValues?.businessName ?? '', props.initialValues?.taxId ?? '');
  }, [props.isOpen, props.Provider, props.initialValues?.businessName, props.initialValues?.taxId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Guardado OK: se cierra y se avisa. Sólo si el envío salió de este formulario (las banderas
  // del store quedan prendidas de operaciones anteriores).
  useEffect(() => {
    if (external || !submitted || !saveSucceeded) return;
    dispatchToast(
      <AppToast intent="success" title={t(isEdit ? 'providerForm.toast.updated' : 'providerForm.toast.created')} body={businessName.trim()} />,
      { intent: 'success', timeout: toastTimeout('success') }
    );
    resetForm();
    props.dismissPanel();
  }, [submitted, saveSucceeded]); // eslint-disable-line react-hooks/exhaustive-deps

  const errorKey = validateBusinessName(businessName);
  const fieldError = submitted && errorKey
    ? t(errorKey === 'required' ? 'providerForm.formValidation.requiredBusinessName' : 'providerForm.formValidation.businessNameTooLong', { max: BUSINESS_NAME_MAX })
    : undefined;
  const taxIdErrorKey = props.showTaxId ? validateTaxId(taxId) : undefined;
  const taxIdError = submitted && taxIdErrorKey ? t(`providerForm.taxId.${taxIdErrorKey}`) : undefined;

  const submitExternal = async () => {
    setExternalSaving(true);
    setExternalError(undefined);
    try {
      await props.onSubmit!(buildInvoiceProviderValues(businessName, props.showTaxId ? taxId : undefined));
      setExternalSaving(false);
      resetForm();
      props.dismissPanel();
    } catch (error: unknown) {
      setExternalSaving(false);
      const typed = error as Partial<ErrorType> | undefined;
      setExternalError({
        ErrorCode: typed?.ErrorCode ?? 0,
        ErrorMessage: typed?.ErrorMessage ?? (error instanceof Error ? error.message : ''),
        Errors: typed?.Errors ?? [],
      });
    }
  };

  const handleSave = () => {
    if (isSaving) return;
    setSubmitted(true);
    if (validateBusinessName(businessName) || taxIdErrorKey) return;
    if (external) {
      submitExternal();
    } else if (props.Provider) {
      dispatch(mapDispatchToProps.UpdateProvider(buildUpdatedProvider(props.Provider, businessName)));
    } else {
      dispatch(mapDispatchToProps.AddProvider(buildNewProvider(businessName)));
    }
  };

  const handleCancel = () => {
    resetForm();
    props.dismissPanel();
  };

  const serverError = external ? externalError : submitted && saveFailed ? ProviderState?.error : undefined;
  const dirty = external
    ? normalizeBusinessName(businessName) !== normalizeBusinessName(props.initialValues?.businessName) || taxId.trim() !== (props.initialValues?.taxId ?? '').trim()
    : isProviderFormDirty(props.Provider, businessName);

  const eyebrow = props.eyebrow ?? (isEdit ? t('providerForm.panel.editingEyebrow', { id: props.Provider?.id, name: props.Provider?.person?.businessName ?? '' }) : t('providerForm.panel.newEyebrow'));

  return (
    <GuideDrawer
      open={props.isOpen}
      size="small"
      eyebrow={eyebrow}
      title={t('providerForm.panel.supplierInformation')}
      subtitle={props.subtitle ?? t(isEdit ? 'providerForm.panel.editingSubtitle' : 'providerForm.panel.newSubtitle')}
      onDismiss={handleCancel}
      preventDismiss={isSaving}
      confirmDiscard={dirty && !isSaving}
      testId="provider-drawer"
      secondaryAction={{ label: t('providerForm.footer.cancelButton'), onClick: handleCancel }}
      primaryAction={{
        label: t(isEdit ? 'providerForm.footer.saveButton' : 'providerForm.footer.createButton'),
        onClick: handleSave,
        loading: isSaving
      }}
    >
      {serverError && (
        <GuideMessageBar intent="error" layout="multiline" data-testid="provider-form-error">
          <MessageBarBody>
            <MessageBarTitle>{t('providerForm.panel.saveFailed')}</MessageBarTitle>
            {serverError.ErrorMessage}
            {serverError.Errors && serverError.Errors.length > 0 && (
              <ul className="provider-form__error-list">
                {serverError.Errors.map((item: string) => <li key={item}>{item}</li>)}
              </ul>
            )}
          </MessageBarBody>
        </GuideMessageBar>
      )}

      <GuideDrawerForm onSubmit={handleSave} testId="provider-form">
        <GuideDrawerSection
          title={t('providerForm.sections.identity')}
          description={t('providerForm.sections.identityDescription')}
        >
          <GuideField
            label={t('providerForm.formFields.businessName')}
            required
            hint={t('providerForm.formFields.businessNameHint')}
            counter={{ value: businessName.length, max: BUSINESS_NAME_MAX }}
            error={fieldError}
          >
            <Input
              name="txt_fullName"
              type="text"
              autoComplete="off"
              autoFocus
              maxLength={BUSINESS_NAME_MAX}
              placeholder={t('providerForm.formFields.businessNamePlaceholder')}
              value={businessName}
              disabled={isSaving}
              onChange={(_event, data) => setBusinessName(data.value)}
            />
          </GuideField>
          {props.showTaxId && (
            <GuideField label={t('providerForm.taxId.label')} hint={t('providerForm.taxId.hint')} error={taxIdError}>
              <Input
                name="txt_taxId"
                type="text"
                inputMode="numeric"
                autoComplete="off"
                maxLength={TAX_ID_INPUT_MAX}
                placeholder={t('providerForm.taxId.placeholder')}
                value={taxId}
                disabled={isSaving}
                onChange={(_event, data) => setTaxId(data.value)}
                data-testid="provider-form-tax-id"
              />
            </GuideField>
          )}
          <GuideField label={t('providerForm.formFields.type')} hint={t('providerForm.formFields.typeHint')}>
            <Input value={t('providerForm.formFields.company')} disabled readOnly />
          </GuideField>
        </GuideDrawerSection>
      </GuideDrawerForm>
    </GuideDrawer>
  );
};

const mapStateToProps = (state: AppState) => ({
  ...state.Provider
});

const mapDispatchToProps = {
  ...ProviderStore.actionCreators
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(ProviderFormComponent as any);
